import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Award, Home, Layers, Briefcase, MessageSquare, ChevronDown } from 'lucide-react';
import { navigationLinks, contactInfo } from '../data';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 text-stone-900">
      {/* Top bar with guarantee - only on desktop */}
      <div className="hidden md:block py-2 bg-stone-900 text-white font-medium text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <span className="flex items-center gap-1.5 justify-center">
            <Award className="h-4 w-4 text-white" />
            {contactInfo.guarantee}
          </span>
          <span className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 text-white" />
            Ring i dag: <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="hover:underline font-semibold">{contactInfo.phone}</a>
          </span>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="bg-white shadow-sm border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between min-h-[4.5rem] xl:min-h-[5.5rem] py-1.5 xl:py-2">
            
            {/* Top row: Logo on all, plus Hamburger on mobile */}
            <div className="flex items-center justify-between w-full h-16 sm:h-20 xl:h-auto xl:w-auto">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 group">
                <img 
                  src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/stenbelaegning-logo-2.png" 
                  alt="Stenbelægning.dk logo" 
                  className="h-12 sm:h-15 xl:h-18 w-auto object-contain shrink-0 group-hover:opacity-90 transition-opacity bg-transparent" 
                />
                <span className="sr-only">
                  Stenbelægning.dk
                </span>
              </Link>

              {/* Mobile menu button */}
              <div className="flex xl:hidden items-center">
                <button
                  onClick={() => setIsOpen(true)}
                  type="button"
                  className="inline-flex items-center justify-center focus:outline-none text-stone-900 hover:text-stone-600 p-1"
                  aria-controls="mobile-menu"
                  aria-expanded={isOpen}
                >
                  <span className="sr-only">Åbn menu</span>
                  <Menu className="block h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Tablet & Desktop Navigation Container */}
            <div className="hidden xl:flex flex-row items-center justify-between gap-8 w-auto">
              <nav className="flex items-center gap-x-6">
                {navigationLinks.map((link) => {
                  if (link.children) {
                    const currentUrl = location.pathname + location.hash;
                    const isChildActive = link.children.some(
                      child => location.pathname === child.path || currentUrl === child.path
                    );
                    return (
                      <div key={link.name} className="relative group py-5">
                        <button
                          className={`flex items-center gap-1 text-xs font-bold tracking-wider uppercase transition-colors cursor-pointer ${
                            isChildActive ? 'text-stone-900' : 'text-stone-600 hover:text-stone-900'
                          }`}
                        >
                          {link.name}
                          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
                        </button>
                        {/* Dropdown menu */}
                        <div className="absolute left-0 top-[100%] mt-1 w-64 rounded-xl shadow-xl bg-white border border-stone-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-2">
                          {link.children.map((child) => {
                            const isChildLinkActive =
                              location.pathname === child.path ||
                              (child.path.includes('#') && currentUrl === child.path);
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block px-5 py-3 text-xs font-bold tracking-wider uppercase transition-colors ${
                                  isChildLinkActive
                                    ? 'bg-stone-50 text-stone-900 border-l-4 border-stone-900'
                                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                                }`}
                              >
                                {child.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  const currentUrl = location.pathname + location.hash;
                  const isActive =
                    location.pathname === link.path ||
                    (link.path.includes('#') && currentUrl === link.path);
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`py-1 text-xs font-bold tracking-wider uppercase transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-stone-900 after:transition-transform after:duration-200 ${
                        isActive
                          ? 'text-stone-900 after:scale-x-100'
                          : 'text-stone-600 hover:text-stone-900 after:scale-x-0 hover:after:scale-x-100'
                      }`}
                    >
                      {link.name}
                    </Link>
                  );
                })}
              </nav>

              {/* CTA Button */}
              <div>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md text-xs font-bold tracking-wider uppercase transition-all duration-200 hover:scale-[1.02] bg-stone-900 text-white hover:bg-stone-800"
                >
                  Få et tilbud
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Mobile Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-stone-200/80 z-40 py-2 px-3 shadow-lg flex justify-around items-center">
        <Link
          to="/"
          className={`flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
            location.pathname === '/' ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          <Home className="h-5 w-5" />
          <span className="text-[10px] font-bold tracking-wider uppercase">Forside</span>
        </Link>

        <Link
          to="/helstoebt-stenbelaegning"
          className={`flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
            location.pathname === '/helstoebt-stenbelaegning' ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          <Layers className="h-5 w-5" />
          <span className="text-[10px] font-bold tracking-wider uppercase">Belægning</span>
        </Link>

        <Link
          to="/referencer"
          className={`flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
            location.pathname.startsWith('/referencer') ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          <Briefcase className="h-5 w-5" />
          <span className="text-[10px] font-bold tracking-wider uppercase">Referencer</span>
        </Link>

        <Link
          to="/kontakt"
          className={`flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
            location.pathname === '/kontakt' ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-[10px] font-bold tracking-wider uppercase">Kontakt</span>
        </Link>

        <button
          onClick={() => setIsOpen(true)}
          className={`flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
            isOpen ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
          }`}
        >
          <Menu className="h-5 w-5" />
          <span className="text-[10px] font-bold tracking-wider uppercase">Menu</span>
        </button>
      </div>

      {/* Mobile Sidebar Menu (Drawer) */}
      <div className={`xl:hidden fixed inset-0 z-50 transition-all duration-300 ${isOpen ? 'visible' : 'invisible'}`}>
        {/* Backdrop overlay */}
        <div 
          className={`absolute inset-0 bg-stone-950/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsOpen(false)}
        />

        {/* Sidebar Drawer Panel */}
        <div 
          className={`absolute top-0 right-0 bottom-0 w-[300px] max-w-[85vw] bg-white shadow-2xl p-6 flex flex-col justify-between transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full overflow-hidden">
            {/* Sidebar Header: Logo & Close Button */}
            <div className="flex items-center justify-between pb-6 border-b border-stone-100 shrink-0">
              <Link to="/" className="flex items-center gap-3" onClick={() => setIsOpen(false)}>
                <img 
                  src="https://assets.auxodev.dk/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/stenbelaegning-logo-2.png" 
                  alt="Stenbelægning.dk logo" 
                  className="h-12 sm:h-14 w-auto object-contain shrink-0 bg-transparent" 
                />
                <span className="sr-only">
                  Stenbelægning.dk
                </span>
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                type="button"
                className="text-stone-900 hover:text-stone-600 focus:outline-none"
              >
                <span className="sr-only">Luk menu</span>
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="mt-6 space-y-4 overflow-y-auto pr-1 flex-grow">
              {navigationLinks.map((link) => {
                if (link.children) {
                  return (
                    <div key={link.name} className="space-y-2 pt-2">
                      <span className="block text-xs font-bold tracking-widest uppercase text-stone-400 pl-4">
                        {link.name}
                      </span>
                      <div className="pl-4 space-y-2 border-l border-stone-100">
                        {link.children.map((child) => {
                          const currentUrl = location.pathname + location.hash;
                          const isChildActive =
                            location.pathname === child.path ||
                            (child.path.includes('#') && currentUrl === child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setIsOpen(false)}
                              className={`block py-1.5 text-xs font-bold tracking-wider uppercase transition-colors ${
                                isChildActive
                                  ? 'text-stone-900 pl-2 border-l-2 border-stone-900'
                                  : 'text-stone-600 hover:text-stone-900 pl-2'
                              }`}
                            >
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-2 text-sm font-bold tracking-wider uppercase transition-colors ${
                      isActive
                        ? 'text-stone-900 border-l-4 border-stone-900 pl-3'
                        : 'text-stone-600 hover:text-stone-900 pl-4'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="pt-6 border-t border-stone-100 shrink-0">
            <p className="text-xs text-stone-500 mb-4 font-medium">{contactInfo.guarantee}</p>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center justify-center px-4 py-3 rounded-md text-sm font-bold tracking-wider uppercase bg-stone-900 text-white hover:bg-stone-800 transition-colors"
            >
              Få et tilbud
            </Link>
            <div className="mt-4 text-center">
              <a 
                href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} 
                className="text-xs text-stone-600 hover:text-stone-900 font-semibold"
              >
                Tlf: {contactInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
